import { action, computed, makeObservable, observable, runInAction, type ObservableMap } from 'mobx';

import { getInboxWorkflow, type InboxWorkflow, type InboxWorkflowAction } from 'api/workflows/endpoints/getInboxWorkflow';
import { toError } from 'helpers/toError';
import { MobxQuery, answerOf } from 'mobx/MobxQuery';
import { markWorkflowAsRead, respondToWorkflowAction, saveWorkflowAction } from 'redux/slices/workflowsSlice';
import type { AppDispatch } from 'redux/store';
import { refuseDegraded } from 'validation';

/**
 * A free-text input, named by its label, or a dropdown parameter, named by its name. The two are saved separately, so
 * an input and a dropdown that share a name are still two fields.
 */
export type FieldKind = 'input' | 'dropdown';

const fieldKey = (kind: FieldKind, name: string): string => `${kind}/${name}`;

/** A draft is keyed by its action and its field; the id prefix is numeric, so `1/` never matches `12/x`. */
const draftKey = (actionId: number, field: string): string => `${actionId}/${field}`;

/** What the record says each field holds, by its field key. */
const seedsOf = (action: InboxWorkflowAction): Map<string, string> => {
  const seeds = new Map<string, string>();
  for (const input of action.response_inputs) seeds.set(fieldKey('input', input.label), input.value);
  for (const param of action.dropdown_parameters) {
    seeds.set(fieldKey('dropdown', param.name), (param.out_value ?? param.value ?? param.submit_value ?? param.default_expression ?? '').trim());
  }
  return seeds;
};

/**
 * MobX-backed behaviour for the inbox's workflow detail panel: one workflow, read by its id, and the operator's
 * unsaved input on its open actions.
 *
 * The workflow is read by id and never taken from the sidebar's paged list: a page-1 reload replaces that list, and a
 * deep-linked workflow beyond the loaded pages was never in it. The id is fixed for the model's lifetime, so a panel
 * showing another workflow needs another model; reusing one would show the previous workflow, with its response
 * buttons live, while the next one loads.
 *
 * Form state is drafts over the record, not a copy of it: a field shows its draft if the operator typed one, and the
 * record's value otherwise. So a re-read never overwrites what was typed, and a save needs no reset — once the re-read
 * lands, the record agrees with the drafts and there is nothing left to save.
 */
export class WorkflowDetailsModel {
  private readonly _dispatch: AppDispatch;
  private readonly _drafts: ObservableMap<string, string> = observable.map();
  private readonly _query: MobxQuery<number, InboxWorkflow | null>;
  private readonly _workflowId: number;
  /** Why the last save or response failed, until the operator dismisses it or writes again. */
  private _writeError: Error | null = null;
  private _writing = false;

  constructor(workflowId: number, dispatch: AppDispatch) {
    this._workflowId = workflowId;
    this._dispatch = dispatch;
    // The operator responds to what this shows, so a workflow that could not be read in full is refused, not shown.
    this._query = new MobxQuery({ params: () => workflowId, fetch: (id: number) => getInboxWorkflow(id).then(refuseDegraded), debounceMs: 0 });
    makeObservable<WorkflowDetailsModel, '_writeError' | '_writing' | '_seeds'>(this, {
      _seeds: computed,
      _writeError: observable.ref,
      _writing: observable,
      busy: computed,
      dismissWriteError: action,
      setInput: action,
      workflow: computed,
    });
  }

  /**
   * True while a write or the re-read after it is in flight. What is shown then may still offer an action that has
   * just been answered, and the respond route does not refuse a second answer: it overwrites the first and moves the
   * flow again.
   */
  get busy(): boolean {
    return this._writing || this._query.value.kind === 'stale';
  }

  /** The record's field values per action, from whichever answer is held. */
  private get _seeds(): ReadonlyMap<number, ReadonlyMap<string, string>> {
    const workflow = answerOf(this._query.value)?.value ?? null;
    return new Map((workflow?.workflows_actions ?? []).map((a) => [a.workflow_action_id, seedsOf(a)]));
  }

  /**
   * What the panel has to show. A failed read is an `Error` even when an earlier answer is held: the failure is nearly
   * always the re-read after a write, so that answer predates the write and would offer the action just answered,
   * without `busy` to hold its buttons. A re-read in flight keeps showing the workflow; `busy` covers it.
   */
  get workflow(): 'loading' | 'not-found' | Error | InboxWorkflow {
    const value = this._query.value;
    switch (value.kind) {
      case 'uninitialized':
      case 'loading':
        return 'loading';
      case 'error':
        return value.error;
      case 'resolved':
        return value.answer.value ?? 'not-found';
      case 'stale':
        return value.previous.value ?? 'not-found';
    }
  }

  get writeError(): Error | null {
    return this._writeError;
  }

  /** Whether an action holds typed input the record does not, and any of its fields has a value worth saving. */
  canSave(actionId: number): boolean {
    const seeds = this._seeds.get(actionId) ?? new Map<string, string>();
    const prefix = draftKey(actionId, '');
    let changed = false;
    const values = new Map(seeds);
    for (const [key, draft] of this._drafts) {
      if (!key.startsWith(prefix)) continue;
      const field = key.slice(prefix.length);
      if (draft !== (seeds.get(field) ?? '')) changed = true;
      values.set(field, draft);
    }
    return changed && [...values.values()].some((v) => v.trim() !== '');
  }

  dismissWriteError(): void {
    this._writeError = null;
  }

  inputValue(actionId: number, kind: FieldKind, name: string): string {
    const field = fieldKey(kind, name);
    return this._drafts.get(draftKey(actionId, field)) ?? this._seeds.get(actionId)?.get(field) ?? '';
  }

  markAsRead(): void {
    void this._dispatch(markWorkflowAsRead(this._workflowId));
  }

  /**
   * The inputs are saved and awaited first, so they are on the server before the response that resumes the flow; if
   * the save fails, no response is sent.
   */
  respond(actionId: number, chosenId: string): Promise<void> {
    return this._write(async () => {
      await this._saveInputs(actionId);
      await this._dispatch(respondToWorkflowAction({ workflow_action_id: actionId, chosen_id: chosenId })).unwrap();
    });
  }

  save(actionId: number): Promise<void> {
    return this._write(() => this._saveInputs(actionId));
  }

  private async _saveInputs(actionId: number): Promise<void> {
    const action = answerOf(this._query.value)?.value?.workflows_actions.find((a) => a.workflow_action_id === actionId);
    if (action === undefined) return;
    await this._dispatch(
      saveWorkflowAction({
        workflow_action_id: actionId,
        response_inputs: action.response_input_options.map((opt) => ({ label: opt.label, value: this.inputValue(actionId, 'input', opt.label) })),
        dropdown_parameters: action.dropdown_parameters.map((param) => ({
          parameter_id: param.parameter_id,
          value: this.inputValue(actionId, 'dropdown', param.name),
        })),
      })
    ).unwrap();
  }

  setInput(actionId: number, kind: FieldKind, name: string, value: string): void {
    this._drafts.set(draftKey(actionId, fieldKey(kind, name)), value);
  }

  /**
   * Runs a write and, if it landed, re-reads the workflow. The refresh turns the query `stale` before `_writing` drops,
   * so `busy` covers the write and the re-read without a gap. A failed write is not re-read: its error is kept for the
   * operator, who retries it, and the drafts it would have saved are still there.
   */
  private async _write(run: () => Promise<unknown>): Promise<void> {
    runInAction(() => {
      this._writing = true;
      this._writeError = null;
    });
    try {
      await run();
      void this._query.refresh();
    } catch (err) {
      runInAction(() => (this._writeError = toError(err)));
    } finally {
      runInAction(() => (this._writing = false));
    }
  }
}
